import { TmElement, watch } from '@thiememeulenhoff/lit-sdk';
import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { clamp } from 'lodash-es';
import { AccordionComponent } from '../accordion/accordion.component';
import { TreeItemComponent } from './tree-item/tree-item.component';
import { styles, tag } from './tree.styles';

function syncCheckboxes(
  changedTreeItem: TreeItemComponent,
  initialSync = false,
) {
  function syncParentItem(treeItem: TreeItemComponent) {
    const children = treeItem.getChildrenItems({ includeDisabled: false });

    if (children.length) {
      const allChecked = children.every(
        (item: TreeItemComponent) => item.selected,
      );
      const allUnchecked = children.every(
        (item: TreeItemComponent) => !item.selected && !item.indeterminate,
      );

      treeItem.setSelected(allChecked);
      treeItem.setIndeterminate(!allChecked && !allUnchecked);
    }
  }

  function syncAncestors(treeItem: TreeItemComponent) {
    const parentItem: TreeItemComponent | null =
      treeItem.parentElement as TreeItemComponent;

    if (TreeItemComponent.isTreeItem(parentItem)) {
      syncParentItem(parentItem);
      syncAncestors(parentItem);
    }
  }

  function syncDescendants(treeItem: TreeItemComponent) {
    treeItem.getChildrenItems().forEach((childItem: TreeItemComponent) => {
      const selected = initialSync
        ? treeItem.selected || childItem.selected
        : !childItem.disabled && treeItem.selected;

      childItem.setSelected(selected);
      syncDescendants(childItem);
    });

    if (initialSync) {
      syncParentItem(treeItem);
    }
  }

  function hasValue(treeItem: TreeItemComponent) {
    return treeItem.value !== '';
  }

  function syncDuplicates(treeItem: TreeItemComponent) {
    if (!hasValue(treeItem)) {
      treeItem.getChildrenItems().forEach(syncDuplicates);

      return;
    }

    const rootElement = treeItem.closest('a-tree');

    if (!rootElement) {
      return;
    }

    const allItems =
      rootElement.querySelectorAll<TreeItemComponent>('a-tree-item');

    allItems.forEach((item) => {
      if (item === treeItem || item.value !== treeItem.value) {
        return;
      }

      item.setSelected(treeItem.selected);
      syncDescendants(item);
      syncAncestors(item);
    });
  }

  syncDescendants(changedTreeItem);
  syncAncestors(changedTreeItem);
  syncDuplicates(changedTreeItem);
}

@customElement(tag)
export class TreeComponent extends TmElement {
  static override styles = [...styles];

  @query('slot:not([name])')
  defaultSlot!: HTMLSlotElement;

  @property()
  selection: 'single' | 'multiple' | 'leaf' = 'single';

  #mutationObserver?: MutationObserver;

  #clickTarget: TreeItemComponent | null = null;

  #lastFocusedItem: TreeItemComponent | null = null;

  override async connectedCallback() {
    super.connectedCallback();

    this.addEventListener('focusin', this.#handleFocusIn);

    this.setAttribute('role', 'tree');

    await this.updateComplete;

    this.#mutationObserver = new MutationObserver(this.#handleTreeChanged);
    this.#mutationObserver.observe(this, { childList: true, subtree: true });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();

    this.#mutationObserver?.disconnect();
  }

  async refreshCheckboxes() {
    await this.updateComplete;

    this.#getAllTreeItems().forEach((item) => syncCheckboxes(item, true));
  }

  #getAllTreeItems() {
    return [...this.querySelectorAll<TreeItemComponent>('a-tree-item')];
  }

  #initTreeItem = (item: TreeItemComponent) => {
    item.setSelectable(this?.selection === 'multiple');
  };

  #handleTreeChanged = (mutations: Array<MutationRecord>) => {
    mutations.forEach((mutation) => {
      const addedNodes: Array<TreeItemComponent> = [
        ...mutation.addedNodes,
      ].filter(TreeItemComponent.isTreeItem) as Array<TreeItemComponent>;
      const removedNodes = [...mutation.removedNodes].filter(
        TreeItemComponent.isTreeItem,
      ) as Array<TreeItemComponent>;

      addedNodes.forEach((node) => {
        this.#initTreeItem(node);
        syncCheckboxes(node, true);
      });

      if (
        this.#lastFocusedItem &&
        removedNodes.includes(this.#lastFocusedItem)
      ) {
        this.#lastFocusedItem = null;
      }
    });
  };

  #selectItem(selectedItem: TreeItemComponent) {
    const previousSelection = [...this.selectedItems];

    if (this.selection === 'multiple') {
      selectedItem.setSelected(!selectedItem.selected);
      syncCheckboxes(selectedItem);
    } else if (this.selection === 'single' || selectedItem.isLeaf) {
      const items = this.#getAllTreeItems();
      items.forEach((item) => {
        item.setSelected(item === selectedItem);
      });
    } else if (this.selection === 'leaf') {
      selectedItem.setExpanded(!selectedItem.expanded);
    }

    const nextSelection = this.selectedItems;

    if (
      previousSelection.length !== nextSelection.length ||
      nextSelection.some((item) => !previousSelection.includes(item))
    ) {
      // Wait for the tree items' DOM to update before emitting
      Promise.all(nextSelection.map((el) => el.updateComplete)).then(() => {
        this.dispatchGlobalEvent({
          eventName: 'change',
          detail: { selection: nextSelection },
        });
      });
    }
  }

  #handleClick(event: MouseEvent | KeyboardEvent) {
    const target = event.target as TreeItemComponent;
    const treeItem = target.closest('a-tree-item');
    const isExpandButton = event
      .composedPath()
      .some((el: EventTarget) =>
        (el as HTMLElement)?.classList?.contains('tree-item__expand-button'),
      );

    if (!treeItem || treeItem.disabled || target !== this.#clickTarget) {
      return;
    }

    if (isExpandButton) {
      treeItem.expanded = !treeItem.expanded;
    } else {
      this.#selectItem(treeItem);
    }
  }

  #handleMouseDown(event: MouseEvent | KeyboardEvent) {
    this.#clickTarget = event.target as TreeItemComponent;
  }

  #handleSlotChange() {
    const items = this.#getAllTreeItems();
    items.forEach(this.#initTreeItem);
  }

  @watch('selection')
  async handleSelectionChange() {
    const isSelectionMultiple = this.selection === 'multiple';
    const items = this.#getAllTreeItems();

    this.setAttribute(
      'aria-multiselectable',
      isSelectionMultiple ? 'true' : 'false',
    );

    items.forEach((item) => {
      item.setSelectable(isSelectionMultiple);
    });

    if (isSelectionMultiple) {
      await this.updateComplete;

      [...this.querySelectorAll(':scope > a-tree-item')].forEach(
        (treeItem: Element) =>
          syncCheckboxes(treeItem as TreeItemComponent, true),
      );
    }
  }

  get selectedItems() {
    const items = this.#getAllTreeItems();
    const isSelected = (item: TreeItemComponent) => item.selected;

    return items.filter(isSelected);
  }

  #handleFocusIn = (event: FocusEvent) => {
    const target = event.target as HTMLElement;
    if (target.localName === 'a-tree-item') {
      const target = event.target as TreeItemComponent;

      // If the tree has been focused, move the focus to the last focused item
      if (event.target === this && TreeItemComponent.isTreeItem(target)) {
        this.#focusItem(this.#lastFocusedItem || this.#getAllTreeItems()[0]);
      }

      // If the target is a tree item, update the tabindex
      if (TreeItemComponent.isTreeItem(target) && !target.disabled) {
        if (this.#lastFocusedItem) {
          this.#lastFocusedItem.tabIndex = -1;
        }
        this.#lastFocusedItem = target;
        this.tabIndex = -1;
      }
    }
  };

  #getFocusableItems() {
    const items = this.#getAllTreeItems();
    const collapsedItems = new Set();

    return items.filter((item) => {
      // Exclude disabled elements
      if (item.disabled) return false;

      // Exclude those whose parent is collapsed
      const parent: TreeItemComponent | null | undefined =
        item.parentElement?.closest('[role=treeitem]');
      if (parent && (!parent.expanded || collapsedItems.has(parent))) {
        collapsedItems.add(item);
      }

      return !collapsedItems.has(item);
    });
  }

  #focusItem(item?: TreeItemComponent | null) {
    item?.focus();
  }

  #focusGroupItem(item?: AccordionComponent | null) {
    item?.shadowRoot?.querySelector('summary')?.focus();
  }

  #focusItemAt = (index: number, items: Array<TreeItemComponent>) => {
    const item = items[clamp(index, 0, items.length - 1)];
    this.#focusItem(item);
  };

  #handleKeyDown(event: KeyboardEvent) {
    // Ignore key presses we aren't interested in
    if (
      ![
        'ArrowDown',
        'ArrowUp',
        'ArrowRight',
        'ArrowLeft',
        'Home',
        'End',
        'Enter',
        ' ',
      ].includes(event.key)
    ) {
      return;
    }

    // Ignore key presses when focus is inside a text field. This prevents the component from hijacking nested form
    // controls that exist inside tree items.
    if (
      event
        .composedPath()
        .some((el: EventTarget) =>
          ['input', 'textarea'].includes(
            (el as HTMLElement)?.tagName?.toLowerCase(),
          ),
        )
    ) {
      return;
    }

    const target = event.target as HTMLElement;
    const items = this.#getFocusableItems();
    if (items.length > 0) {
      event.preventDefault();
      if (target.localName === 'a-tree-item') {
        this.#handleKeyDownForTreeItem(event, items);
        // Handle a-tree-item specific logic
      } else if (target.localName === 'a-accordion') {
        this.#handleKeyDownForAccordion(event, items);
      }
    }
  }

  #isFirstChildInGroup(item: TreeItemComponent): boolean {
    const accordion = item.parentElement?.closest('a-accordion');

    if (!accordion || !accordion.children) {
      return false;
    }

    const childrenArray = Array.from(accordion.children);
    return childrenArray[1] === item;
  }

  #handleKeyDownForTreeItem(
    event: KeyboardEvent,
    items: Array<TreeItemComponent>,
  ) {
    const activeItemIndex = items.findIndex((item) => item.matches(':focus'));
    const activeItem: TreeItemComponent | undefined = items[activeItemIndex];
    const isLtr = this.matches(':dir(ltr)');
    const isRtl = this.matches(':dir(rtl)');

    if (event.key === 'ArrowDown') {
      // Moves focus to the next node that is focusable without opening or closing a node.
      this.#focusItemAt(activeItemIndex + 1, items);
    } else if (event.key === 'ArrowUp') {
      // Moves focus to the next node that is focusable without opening or closing a node.
      if (this.#isFirstChildInGroup(activeItem)) {
        // close the group item / accordion
        const groupItem = activeItem.parentElement?.closest(
          'a-accordion',
        ) as AccordionComponent;
        groupItem.open = false;

        this.#focusGroupItem(groupItem);
      } else {
        this.#focusItemAt(activeItemIndex - 1, items);
      }
    } else if (
      (isLtr && event.key === 'ArrowRight') ||
      (isRtl && event.key === 'ArrowLeft')
    ) {
      //
      // When focus is on a closed node, opens the node; focus does not move.
      // When focus is on a open node, moves focus to the first child node.
      // When focus is on an end node (a tree item with no children), does nothing.
      //
      if (
        !activeItem ||
        activeItem.disabled ||
        activeItem.expanded ||
        activeItem.isLeaf
      ) {
        this.#focusItemAt(activeItemIndex + 1, items);
      } else {
        activeItem.expanded = true;
      }
    } else if (
      (isLtr && event.key === 'ArrowLeft') ||
      (isRtl && event.key === 'ArrowRight')
    ) {
      //
      // When focus is on an open node, closes the node.
      // When focus is on a child node that is also either an end node or a closed node, moves focus to its parent node.
      // When focus is on a closed `tree`, does nothing.
      //
      if (
        !activeItem ||
        activeItem.disabled ||
        activeItem.isLeaf ||
        !activeItem.expanded
      ) {
        this.#focusItemAt(activeItemIndex - 1, items);
      } else {
        activeItem.expanded = false;
      }
    } else if (event.key === 'Home') {
      // Moves focus to the first node in the tree without opening or closing a node.
      this.#focusItemAt(0, items);
    } else if (event.key === 'End') {
      // Moves focus to the last node in the tree that is focusable without opening the node.
      this.#focusItemAt(items.length - 1, items);
    } else if (event.key === 'Enter' || event.key === ' ') {
      // Selects the focused node.
      if (!activeItem.disabled) {
        this.#selectItem(activeItem);
      }
    }
  }

  #handleKeyDownForAccordion(
    event: KeyboardEvent,
    items: Array<TreeItemComponent>,
  ) {
    const target = event.target as AccordionComponent;
    const activeItemIndex = items.findIndex(
      (item) => item.parentElement?.closest('a-accordion') === target,
    );
    const activeItem: TreeItemComponent | undefined = items[activeItemIndex];
    const groupItem = activeItem.parentElement?.closest(
      'a-accordion',
    ) as AccordionComponent;

    if (event.key === 'Enter') {
      if (target.open) {
        // close the group item / accordion
        target.open = false;
        this.#focusGroupItem(groupItem);
      } else {
        target.open = !target.open;
        this.#focusItemAt(activeItemIndex, items);
      }
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      target.open = !target.open;
    } else if (event.key === 'ArrowDown') {
      if (target.open === true) {
        // navigate to first item in current accordion
        this.#focusItemAt(activeItemIndex, items);
      } else {
        target.open = !target.open;
        this.#focusGroupItem(groupItem);
      }
    } else if (event.key === 'ArrowUp') {
      if (this.#isFirstChildInGroup(activeItem)) {
        target.open = false;
        this.#focusGroupItem(groupItem);
      }
    }
  }

  override render() {
    return html`
      <div
        part="base"
        @click=${this.#handleClick}
        @mousedown=${this.#handleMouseDown}
        @keydown=${this.#handleKeyDown}
      >
        <slot @slotchange=${this.#handleSlotChange}></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-tree': TreeComponent;
  }
}
